import {GridEvent, GridEventType} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridEvent';
import {
    CellEvent,
    CellRenderContext,
    IGridModelView,
    IMutableGridModel,
    OnCellEvent,
    RowIndex
} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import {GridColumnWidthChanged} from '@mintdata-wsp/wsp/ui/components/grid/GridEvent';
import {ResizeEvent, Resizer} from '@mintdata-wsp/wsp/ui/components/resizable/Resizer';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {AutoSizer, defaultCellRangeRenderer, Grid, GridCellProps, Index} from 'react-virtualized';
import {GridCellRangeProps} from 'react-virtualized/dist/es/Grid';

const styles = require('./DataGridView.less');
const cx = classnames.bind(styles);

export interface DataGridViewProps //extends PanelContentProps
{
    gridModel:IMutableGridModel;
    className?:string;
}

@observer
export class DataGridView extends React.Component<DataGridViewProps, any>
{
    @observable
    private _numRows:number;

    @observable
    private _numColumns:number;

    private _grid:Grid = null;

    constructor(props:DataGridViewProps)
    {
        super(props);
        this._init(props);
    }

    componentWillReceiveProps(nextProps: Readonly<DataGridViewProps>, nextContext: any): void
    {
        this._init(nextProps);
    }

    componentWillUnmount(): void
    {
        this._grid = null;
    }

    @action.bound
    private _init(props:DataGridViewProps)
    {
        this._initNumRowsAndColumns();
        this._listenForGridChanges();
    }

    @bind
    private _initNumRowsAndColumns()
    {
        const view = this._getGridView();

        this._numRows = view.getNumRows();
        this._numColumns = view.getNumColumns();

        //console.log(`ID#${this.props.gridModel.getId()} DataGridView::_initNumRowsAndColumns: rows: ${this._numRows} cols: ${this._numColumns}`);
    }

    @bind
    private _getGridView():IGridModelView
    {
        const {gridModel} = this.props;
        const contentView = gridModel.getContentView();

        return contentView;
    }

    @action.bound
    private _listenForGridChanges()
    {
        const gridModel = this.props.gridModel;
        gridModel.addListener(this._onGridChanged);
    }

    @action.bound
    private _onGridChanged(e:GridEvent)
    {
        this._initNumRowsAndColumns();

        if( e.type === GridEventType.ColumnWidthChanged )
        {
            if( this._grid )
            {
                const columnIndex = (e as GridColumnWidthChanged).columnIndex;
                this._grid.invalidateCellSizeAfterRender({columnIndex: columnIndex, rowIndex:0});
                this._grid.forceUpdate();
            }
        }
    }

    @bind
    private _createCellEventHandler(rowIndex:RowIndex):OnCellEvent
    {
        return (e:CellEvent)=>
        {
            const view = this._getGridView();
            view.setIsRowSelected(rowIndex, true); // !view.isRowSelected(rowIndex));

            this._grid.forceUpdate();
        }
    }

    @bind
    private _cellRenderer(cellProps:GridCellProps)
    {
        const {rowIndex, columnIndex, parent, style} = cellProps;
        const {gridModel} = this.props;

        this._grid = parent as Grid;

        const gridView = this._getGridView();
        const cellArea = gridView.getAreaAt(rowIndex, columnIndex);
        const areaStyles = gridModel.getAppearance().getCellAreaStyles();

        const isRowSelected = this._getGridView().isRowSelected(rowIndex);
        const cellClassNames = areaStyles.getCellClassNames(cellArea, isRowSelected);

        // console.log(`DataGridView::cellRenderer(${rowIndex}, ${columnIndex}) isRowSelected? ${isRowSelected}`);

        const cell = gridView.getCellAt(rowIndex, columnIndex);
        if( !cell )
        {
            console.error(`Error: cannot find cell at ${rowIndex}, ${columnIndex} _numRows: ${this._numRows}, _numCols: ${this._numColumns}`);
            return null;
        }

        const reactKey = `cell-${rowIndex}-${columnIndex}`;
        const renderer = this.props.gridModel.getAppearance().getRendererFor(cell);
        const ctx = new CellRenderContext(false, cell, reactKey, style, this._createCellEventHandler(rowIndex), cellClassNames, null);

        const reactElement = renderer.render(ctx);
        return reactElement;
    }

    @bind
    _customCellRangeRenderer(params:GridCellRangeProps)
    {
        const rangeRendererFn = defaultCellRangeRenderer;

        const {columnStartIndex, columnStopIndex, columnSizeAndPositionManager, horizontalOffsetAdjustment, parent} = params;
        const children:Array<React.ReactNode> = rangeRendererFn(params);

        for( let columnIndex = columnStartIndex; columnIndex <= columnStopIndex; columnIndex++ )
        {
            const columnDatum = columnSizeAndPositionManager.getSizeAndPositionOfCell(columnIndex);

            const left = columnDatum.offset + horizontalOffsetAdjustment;
            const width = columnDatum.size;
            const resizerX = left + width;
            const resizerWidthPx = 8;

            const style =
            {
                position: 'absolute',
                left: resizerX - (resizerWidthPx/2),
                top: 0,
                bottom: 0
            };

            children.push(<Resizer key={`resizer-col-${columnIndex}`}
                                   className={cx('rv-resizer')}
                                   isVisible={true}
                                   style={style}
                                   isVertical={true}
                                   onResize={this._onResized(columnIndex, parent as Grid)}/>);
        }

        return children;
    }

    @bind
    _onResized(columnIndex:number, grid:Grid)
    {
        return (resizeEvent:ResizeEvent)=>
        {
            // console.log(`resized: columnIndex: ${columnIndex} resizeEvent: ${resizeEvent.deltaX} x: ${resizeEvent.x}`);

            const view = this._getGridView();

            const curWidth = view.getColumnWidth(columnIndex);
            const newWidth = curWidth + resizeEvent.deltaX;

            if( newWidth < 10 )
                return;

            view.setColumnWidth(columnIndex, newWidth);
            grid.invalidateCellSizeAfterRender({columnIndex:columnIndex, rowIndex:0});
            grid.forceUpdate();
        };
    }

    @bind
    _getColumnWidth(gridIndex:Index):number
    {
        const columnIndex = gridIndex.index;
        const view = this._getGridView();
        const columnWidth = view.getColumnWidth(columnIndex);

        // console.log(`get width(${gridIndex}) => ${columnWidth}`);

        return columnWidth;
    }

    render()
    {
        const {className, gridModel} = this.props;

        // Note: `this._numDataRows` and `this._numColumns` *must* be read here, since inner <Grid> is
        // called in a different/later scope. As a result, without this read here,
        // we don't re-render the component when the data source (props.gridModel) changes.
        const rowCount = this._numRows;
        const columnCount = this._numColumns;
        const hasBorder = gridModel.getAppearance().hasBorder;

        return (
            <div style={{ flex: '1 1 auto' }}>
                <AutoSizer>
                    {
                        ({width, height})=>
                        {
                            return  <Grid   className={cx('rv-grid',
                                                          {'rv-grid-has-border': hasBorder},
                                                          className
                                                         )}
                                            width={width}
                                            height={height}
                                            rowCount={rowCount}
                                            rowHeight={25}
                                            columnCount={columnCount}
                                            columnWidth={this._getColumnWidth}
                                            overscanColumnCount={0}
                                            overscanRowCount={2}
                                            cellRenderer={this._cellRenderer as any}
                                            cellRangeRenderer={this._customCellRangeRenderer as any}
                                    />
                        }
                    }
                </AutoSizer>
            </div>
        );
    }
}
